import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaSave,
  FaTimes,
  FaBook,
  FaGripVertical,
  FaGlobe,
  FaFileAlt,
  FaVideo,
  FaChartBar,
  FaDownload,
  FaEye,
  FaFilePdf,
  FaFileExcel,
  FaFileWord,
  FaCalculator,
  FaCloudUploadAlt
} from 'react-icons/fa';
import './AdminResources.css';

const CATEGORY_OPTIONS = [
  { value: 'guides', label: 'Guides', icon: <FaBook /> },
  { value: 'whitepapers', label: 'Whitepapers', icon: <FaFileAlt /> },
  { value: 'casestudies', label: 'Case Studies', icon: <FaChartBar /> },
  { value: 'webinars', label: 'Webinars', icon: <FaVideo /> }
];

const TYPE_OPTIONS = [
  { value: 'guide', label: 'Guide', label_fr: 'Guide' },
  { value: 'guide_technique', label: 'Technical Guide', label_fr: 'Guide Technique' },
  { value: 'guide_pratique', label: 'Practical Guide', label_fr: 'Guide Pratique' },
  { value: 'livre_blanc', label: 'Whitepaper', label_fr: 'Livre Blanc' },
  { value: 'etude_de_cas', label: 'Case Study', label_fr: 'Étude de Cas' },
  { value: 'webinaire', label: 'Webinar', label_fr: 'Webinaire' },
  { value: 'outil', label: 'Tool', label_fr: 'Outil' }
];

const AdminResources = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resourcesList, setResourcesList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [resourceFile, setResourceFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    // English content
    title_en: '',
    description_en: '',
    type_en: '',
    pages_en: '',
    readTime_en: '',
    // French content
    title_fr: '',
    description_fr: '',
    type_fr: '',
    pages_fr: '',
    readTime_fr: '',
    // Common fields
    category: 'guides',
    imageUrl: '',
    downloadUrl: '',
    previewUrl: '',
    order: 0,
    isPopular: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchResources();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchResources = async () => {
    try {
      const q = query(collection(db, 'resources'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setResourcesList(items);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to load resources');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleResourceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
        return;
      }
      setResourceFile(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fileName = `resources/images/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'storage/unauthorized') {
        throw new Error('Permission denied. Please update Firebase Storage rules.');
      }
      throw err;
    }
  };

  const uploadResourceFile = async (file) => {
    try {
      setUploadingFile(true);
      const fileName = `resources/files/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('File upload error:', err);
      if (err.code === 'storage/unauthorized') {
        throw new Error('Permission denied. Please update Firebase Storage rules.');
      }
      throw err;
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (fileUrl) => {
    if (fileUrl && fileUrl.includes('firebase')) {
      try {
        const fileRef = ref(storage, fileUrl);
        await deleteObject(fileRef);
      } catch (err) {
        console.log('File deletion error (might not exist):', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title_en: '',
      description_en: '',
      type_en: '',
      pages_en: '',
      readTime_en: '',
      title_fr: '',
      description_fr: '',
      type_fr: '',
      pages_fr: '',
      readTime_fr: '',
      category: 'guides',
      imageUrl: '',
      downloadUrl: '',
      previewUrl: '',
      order: resourcesList.length,
      isPopular: false
    });
    setImagePreview(null);
    setImageFile(null);
    setResourceFile(null);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      title_en: item.title_en || '',
      description_en: item.description_en || '',
      type_en: item.type_en || '',
      pages_en: item.pages_en || '',
      readTime_en: item.readTime_en || '',
      title_fr: item.title_fr || '',
      description_fr: item.description_fr || '',
      type_fr: item.type_fr || '',
      pages_fr: item.pages_fr || '',
      readTime_fr: item.readTime_fr || '',
      category: item.category || 'guides',
      imageUrl: item.imageUrl || '',
      downloadUrl: item.downloadUrl || '',
      previewUrl: item.previewUrl || '',
      order: item.order || 0,
      isPopular: item.isPopular || false
    });
    setImagePreview(item.imageUrl || null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title_en.trim() || !formData.description_en.trim()) {
      setError('English title and description are required.');
      setActiveTab('en');
      return;
    }

    setSaving(true);

    try {
      let imageUrl = formData.imageUrl;
      let downloadUrl = formData.downloadUrl;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      if (resourceFile) {
        downloadUrl = await uploadResourceFile(resourceFile);
      }

      const resourceData = {
        // English content
        title_en: formData.title_en.trim(),
        description_en: formData.description_en.trim(),
        type_en: formData.type_en.trim(),
        pages_en: formData.pages_en.trim(),
        readTime_en: formData.readTime_en.trim(),
        // French content
        title_fr: formData.title_fr.trim(),
        description_fr: formData.description_fr.trim(),
        type_fr: formData.type_fr.trim(),
        pages_fr: formData.pages_fr.trim(),
        readTime_fr: formData.readTime_fr.trim(),
        // Common fields
        category: formData.category,
        imageUrl,
        downloadUrl,
        previewUrl: formData.previewUrl || downloadUrl,
        order: formData.order,
        isPopular: formData.isPopular,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'resources', editingId), resourceData);
        setSuccess('Resource updated successfully!');
      } else {
        resourceData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'resources'), resourceData);
        setSuccess('Resource added successfully!');
      }

      await fetchResources();
      resetForm();
    } catch (err) {
      console.error('Error saving resource:', err);
      setError(err.message || 'Failed to save resource. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title_en}"?`)) {
      return;
    }

    try {
      if (item.imageUrl) {
        await deleteFile(item.imageUrl);
      }
      if (item.downloadUrl) {
        await deleteFile(item.downloadUrl);
      }
      await deleteDoc(doc(db, 'resources', item.id));
      setSuccess('Resource deleted successfully!');
      await fetchResources();
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError('Failed to delete resource');
    }
  };

  const handlePreview = (url) => {
    if (url && url !== '#') {
      window.open(url, '_blank');
    } else {
      setError('No preview available for this resource');
    }
  };

  const handleDownload = (url, title) => {
    if (url && url !== '#') {
      const link = document.createElement('a');
      link.href = url;
      link.download = title || 'resource';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setError('No download available for this resource');
    }
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORY_OPTIONS.find(c => c.value === category);
    return cat ? cat.icon : <FaFileAlt />;
  };

  const getFileIcon = (url) => {
    if (!url) return <FaFileAlt />;
    const lower = url.toLowerCase();
    if (lower.includes('.pdf')) return <FaFilePdf />;
    if (lower.includes('.xls')) return <FaFileExcel />;
    if (lower.includes('.doc')) return <FaFileWord />;
    if (lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov')) return <FaVideo />;
    return <FaFileAlt />;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-resources">
      <header className="admin-resources-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaBook /> Resources Management</h1>
      </header>

      <main className="admin-resources-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="resources-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New Resource
              </button>
            </div>

            <div className="resources-list">
              {resourcesList.length === 0 ? (
                <div className="empty-state">
                  <FaBook />
                  <h3>No resources yet</h3>
                  <p>Click "Add New Resource" to create your first resource.</p>
                </div>
              ) : (
                resourcesList.map((item, index) => (
                  <div key={item.id} className="resource-item">
                    <div className="resource-item-order">
                      <FaGripVertical />
                      <span>{index + 1}</span>
                    </div>
                    <div className="resource-item-image">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title_en} />
                      ) : (
                        <div className="no-image"><FaImage /></div>
                      )}
                    </div>
                    <div className="resource-item-content">
                      <div className="resource-item-category">
                        {getCategoryIcon(item.category)}
                        <span>{item.category}</span>
                        {item.isPopular && <span className="popular-badge">Popular</span>}
                      </div>
                      <div className="resource-item-langs">
                        <span className="lang-badge en">EN: {item.title_en || 'Not set'}</span>
                        <span className="lang-badge fr">FR: {item.title_fr || 'Not set'}</span>
                      </div>
                      <p>{item.description_en}</p>
                      <div className="resource-item-meta">
                        {item.type_en && <span className="meta-tag">{item.type_en}</span>}
                        {item.pages_en && <span className="meta-tag">{item.pages_en}</span>}
                        {item.readTime_en && <span className="meta-tag">{item.readTime_en}</span>}
                      </div>
                    </div>
                    <div className="resource-item-file">
                      {item.downloadUrl && item.downloadUrl !== '#' ? (
                        <div className="file-indicator">
                          {getFileIcon(item.downloadUrl)}
                          <span>File attached</span>
                        </div>
                      ) : (
                        <div className="file-indicator no-file">
                          <FaFileAlt />
                          <span>No file</span>
                        </div>
                      )}
                    </div>
                    <div className="resource-item-actions">
                      <button
                        onClick={() => handlePreview(item.previewUrl || item.downloadUrl)}
                        className="preview-btn"
                        title="Preview"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleDownload(item.downloadUrl, item.title_en)}
                        className="download-btn-small"
                        title="Download"
                      >
                        <FaDownload />
                      </button>
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="resources-form-container">
            <h2>{editingId ? 'Edit Resource' : 'Add New Resource'}</h2>

            {/* Language Tabs */}
            <div className="language-tabs">
              <button
                type="button"
                className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                onClick={() => setActiveTab('en')}
              >
                <FaGlobe /> English
              </button>
              <button
                type="button"
                className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                onClick={() => setActiveTab('fr')}
              >
                <FaGlobe /> French
              </button>
            </div>

            <form onSubmit={handleSubmit} className="resources-form">
              {/* English Content */}
              <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (English) *</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    placeholder="e.g., RPA Implementation Guide"
                  />
                </div>

                <div className="form-group">
                  <label>Description (English) *</label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder="Brief description of this resource..."
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type (English)</label>
                    <select
                      value={formData.type_en}
                      onChange={(e) => setFormData({ ...formData, type_en: e.target.value })}
                    >
                      <option value="">Select type...</option>
                      {TYPE_OPTIONS.map(t => (
                        <option key={t.value} value={t.label}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Pages / Format (English)</label>
                    <input
                      type="text"
                      value={formData.pages_en}
                      onChange={(e) => setFormData({ ...formData, pages_en: e.target.value })}
                      placeholder="e.g., 45 pages, Video, Excel"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Read Time (English)</label>
                  <input
                    type="text"
                    value={formData.readTime_en}
                    onChange={(e) => setFormData({ ...formData, readTime_en: e.target.value })}
                    placeholder="e.g., 30 min, 1h, 2h"
                  />
                </div>
              </div>

              {/* French Content */}
              <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (French)</label>
                  <input
                    type="text"
                    value={formData.title_fr}
                    onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                    placeholder="e.g., Guide d'Implémentation RPA"
                  />
                </div>

                <div className="form-group">
                  <label>Description (French)</label>
                  <textarea
                    value={formData.description_fr}
                    onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                    placeholder="Brève description de cette ressource..."
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type (French)</label>
                    <select
                      value={formData.type_fr}
                      onChange={(e) => setFormData({ ...formData, type_fr: e.target.value })}
                    >
                      <option value="">Sélectionner le type...</option>
                      {TYPE_OPTIONS.map(t => (
                        <option key={t.value} value={t.label_fr}>{t.label_fr}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Pages / Format (French)</label>
                    <input
                      type="text"
                      value={formData.pages_fr}
                      onChange={(e) => setFormData({ ...formData, pages_fr: e.target.value })}
                      placeholder="e.g., 45 pages, Vidéo, Excel"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Read Time (French)</label>
                  <input
                    type="text"
                    value={formData.readTime_fr}
                    onChange={(e) => setFormData({ ...formData, readTime_fr: e.target.value })}
                    placeholder="e.g., 30 min, 1h, 2h"
                  />
                </div>
              </div>

              {/* Common Fields */}
              <div className="common-fields">
                <div className="form-group">
                  <label>Category *</label>
                  <div className="category-selector">
                    {CATEGORY_OPTIONS.map(cat => (
                      <button
                        key={cat.value}
                        type="button"
                        className={`category-option ${formData.category === cat.value ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, category: cat.value })}
                      >
                        {cat.icon}
                        <span>{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isPopular}
                        onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                      />
                      <span>Mark as Popular Resource</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Thumbnail Image</label>
                  <div className="image-upload-area">
                    {imagePreview ? (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setImageFile(null);
                            setFormData({ ...formData, imageUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload thumbnail image</span>
                        <small>Max size: 5MB (JPG, PNG, WebP)</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Resource File (PDF, Video, Excel, etc.)</label>
                  <div className="file-upload-area">
                    {formData.downloadUrl && formData.downloadUrl !== '#' ? (
                      <div className="file-preview">
                        {getFileIcon(formData.downloadUrl)}
                        <span className="file-name">
                          {resourceFile ? resourceFile.name : 'File attached'}
                        </span>
                        <div className="file-actions">
                          <button
                            type="button"
                            onClick={() => handlePreview(formData.downloadUrl)}
                            className="preview-file-btn"
                          >
                            <FaEye /> Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setResourceFile(null);
                              setFormData({ ...formData, downloadUrl: '', previewUrl: '' });
                            }}
                            className="remove-file-btn"
                          >
                            <FaTimes /> Remove
                          </button>
                        </div>
                      </div>
                    ) : resourceFile ? (
                      <div className="file-preview">
                        {getFileIcon(resourceFile.name)}
                        <span className="file-name">{resourceFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setResourceFile(null)}
                          className="remove-file-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="file-upload-label">
                        <FaCloudUploadAlt />
                        <span>Click to upload resource file</span>
                        <small>Max size: 50MB (PDF, DOC, XLS, MP4, etc.)</small>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.webm,.mov"
                          onChange={handleResourceFileChange}
                          hidden
                        />
                      </label>
                    )}
                    {uploadingFile && (
                      <div className="upload-progress">
                        <FaSpinner className="spinner" />
                        <span>Uploading file...</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>External Preview URL (optional)</label>
                  <input
                    type="text"
                    value={formData.previewUrl}
                    onChange={(e) => setFormData({ ...formData, previewUrl: e.target.value })}
                    placeholder="https://example.com/preview (for external links like YouTube)"
                  />
                  <small className="form-help">Use this for webinar videos or external content. Leave empty to use the uploaded file.</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving || uploadingFile} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> Saving...</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'} Resource</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminResources;
